import styled from '@emotion/styled';
import moment from 'moment';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  ECancelButton,
  EDeleteButton,
  EForm,
  EFormButtonRow,
  ELabel,
  ESubmitButton,
} from 'webapp-lib/Components';
import {
  mixinFlexboxColumn,
  mixinFlexboxRow,
  mixinValidationErrorMessage,
} from 'webapp-lib/styling';
import {TIME_FORMAT} from 'webapp-lib/utils';

import {CasusAutocomplete} from '#/Autocomplete';
import {Dialog} from '#/Dialog/Dialog';
import {
  EFTextArea,
  EFTimeInput,
  requiredAndNonWhitespaceRule,
  requiredRule,
  useForm,
} from '_/utilities/form';

import {
  EventApiWithTimEntryProps,
  TimeEntry,
  TimeEntryCasus,
} from '../timetracker.types';

enum EditTimeEntryFormFields {
  CasusId = 'casusId',
  Title = 'title',
  Description = 'description',
  StartTime = 'startTime',
  EndTime = 'endTime',
}

type EditTimeEntryFormInputs = {
  [EditTimeEntryFormFields.CasusId]?: string;
  [EditTimeEntryFormFields.Title]: string;
  [EditTimeEntryFormFields.Description]?: string;
  [EditTimeEntryFormFields.StartTime]: string;
  [EditTimeEntryFormFields.EndTime]: string;
};

const START_TIME_NOT_IN_FUTURE_FOR_ONGOING = 'startTimeNotInFutureForOngoing';
const VALID_TIME_SLOT = 'validTimeSlot';

function applyTimeToDate(date: Date, timeString: string): Date {
  const time = moment(timeString, TIME_FORMAT);
  return moment(date).set({hour: time.hour(), minute: time.minute()}).toDate();
}

const isValidTimeSlot = (startTime: string, endTime: string): boolean => {
  const start = moment(startTime, TIME_FORMAT);
  const end = moment(endTime, TIME_FORMAT);
  if (!start.isValid() || !end.isValid()) {
    return false;
  }

  const durationHours = moment.duration(end.diff(start)).asHours();
  return durationHours >= 0 && durationHours <= 24;
};

export function EditTimeEntryDialog({
  data,
  isOpen,
  onClose,
  onEdit,
  onDelete,
}: {
  data: EventApiWithTimEntryProps;
  isOpen: boolean;
  onEdit: (event: TimeEntry) => void | Promise<void>;
  onDelete: (event: EventApiWithTimEntryProps) => void;
  onClose: () => void;
}): React.ReactElement {
  const {t} = useTranslation('timetracker');
  const [selectedCasus, setSelectedCasus] = useState<TimeEntryCasus>();

  const ongoing = data.extendedProps.ongoing ?? false;

  const {control, formState, clearErrors, getValues, handleSubmit} =
    useForm<EditTimeEntryFormInputs>({
      defaultValues: {
        casusId: data.extendedProps.casus?.id || '',
        title: data.title || '',
        description: data.extendedProps?.description || '',
        startTime: moment(data.start).format(TIME_FORMAT),
        endTime: ongoing ? '' : moment(data.end).format(TIME_FORMAT),
      },
      onSubmit: async ({title, description, startTime, endTime}) => {
        const result: TimeEntry = {
          id: data.id,
          title,
          description,
          casus: selectedCasus,
          start: applyTimeToDate(data.start!, startTime),
          end: ongoing ? data.end! : applyTimeToDate(data.end!, endTime),
        };

        await onEdit(result);
        onClose();
      },
    });

  const clearTimeFieldsErrors = () => {
    clearErrors(EditTimeEntryFormFields.StartTime);
    clearErrors(EditTimeEntryFormFields.EndTime);
  };

  const messageFor = (errorType?: string): string | undefined => {
    switch (errorType) {
      case START_TIME_NOT_IN_FUTURE_FOR_ONGOING:
        return t('validations.startTimeMustNotBeInFuture');
      case VALID_TIME_SLOT:
        return t('validations.endTimeMustBeAfterStartTime');
      default:
        return undefined;
    }
  };

  const timeFieldsErrorMessage =
    messageFor(formState.errors[EditTimeEntryFormFields.StartTime]?.type) ??
    messageFor(formState.errors[EditTimeEntryFormFields.EndTime]?.type);

  const startTimeRules = {
    ...requiredRule,
    validate: {
      [START_TIME_NOT_IN_FUTURE_FOR_ONGOING]: (startTime: string): boolean => {
        clearTimeFieldsErrors();

        return (
          !ongoing ||
          !moment(applyTimeToDate(data.start!, startTime)).isAfter(moment())
        );
      },
      [VALID_TIME_SLOT]: (startTime: string): boolean =>
        ongoing || isValidTimeSlot(startTime, getValues().endTime),
    },
  };

  const endTimeRules = {
    ...requiredRule,
    validate: {
      [VALID_TIME_SLOT]: (endTime: string): boolean => {
        clearTimeFieldsErrors();

        return isValidTimeSlot(getValues().startTime, endTime);
      },
    },
  };

  return (
    <Dialog
      headerText={t('components.timeEntries.title')}
      isShown={isOpen}
      size='tiny'
      onClose={onClose}
    >
      <EForm onSubmit={handleSubmit}>
        <EFTextArea
          control={control}
          label={t('attributes.title')}
          minRows={2}
          name={EditTimeEntryFormFields.Title}
          rules={requiredAndNonWhitespaceRule}
          autoFocus
        />
        <CasusAutocomplete
          control={control}
          label={t('attributes.casus')}
          name={EditTimeEntryFormFields.CasusId}
          searchFiltersPersistenceKey='timetrackerTimeEntryForm'
          onCasusChange={(casus) => {
            setSelectedCasus(
              casus
                ? {id: casus.id, displayName: casus.displayNameWithClient}
                : undefined
            );
          }}
        />
        <EFTextArea
          control={control}
          label={t('attributes.description')}
          minRows={4}
          name={EditTimeEntryFormFields.Description}
        />

        <EventTimeRow>
          <EFTimeInput
            control={control}
            label={t('attributes.start')}
            name={EditTimeEntryFormFields.StartTime}
            rules={startTimeRules}
          />
          <DashColumn>
            <ELabel>&nbsp;</ELabel>
            <span className='dash'>&mdash;</span>
          </DashColumn>
          <EFTimeInput
            control={control}
            disabled={ongoing}
            label={t('attributes.end')}
            name={EditTimeEntryFormFields.EndTime}
            rules={ongoing ? undefined : endTimeRules}
          />
        </EventTimeRow>
        {timeFieldsErrorMessage ? (
          <ValidationMessage>{timeFieldsErrorMessage}</ValidationMessage>
        ) : null}
        <EFormButtonRow>
          <ECancelButton disabled={formState.isSubmitting} onClick={onClose} />
          <ButtonRow>
            <EDeleteButton onClick={() => onDelete(data)} />
            <ESubmitButton isSubmitting={formState.isSubmitting} />
          </ButtonRow>
        </EFormButtonRow>
      </EForm>
    </Dialog>
  );
}

export const ValidationMessage = styled.p`
  ${mixinValidationErrorMessage}
`;

const EventTimeRow = styled.div`
  ${mixinFlexboxRow};
  gap: var(--gutter-little);
  align-items: flex-start;
`;

const DashColumn = styled.div`
  ${mixinFlexboxColumn};

  .dash {
    height: var(--input-min-height);
    line-height: var(--input-min-height);
  }
`;

const ButtonRow = styled.span`
  ${mixinFlexboxRow};
  gap: var(--gutter-little);
  justify-content: space-between;
`;
